import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Button, IconButton, Snackbar, Alert } from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { fetchVehicles, deleteVehicle, fetchCustomers } from "../services/ApiService";
import BulkUploadVehicles from '../components/BulkUploadVehicles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import DataTable from '../components/DataTable';

const COLUMNS = [
  { key: 'imei', label: 'IMEI', render: (row) => row.imei_details?.imei },
  { key: 'registration_number', label: 'Registration No.' },
  { key: 'model_name', label: 'Model Name' },
  { key: 'model_year', label: 'Model Year' },
  { key: 'chassis_number', label: 'Chassis No' },
  { key: 'engine_number', label: 'Engine No' },
  { key: 'color', label: 'Color' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'vehicle_type', label: 'Vehicle Type' },
  { key: 'remarks', label: 'Remarks' },
  { key: 'customer_name', label: 'Customer' },
];

export function AddTruck() {
  const navigate = useNavigate();
  const location = useLocation();
  const [haulierOptions, setCustomerOptions] = React.useState({});
  const [employees, setEmployees] = React.useState([]);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteTargetId, setDeleteTargetId] = React.useState(null);

  const handleDeleteClick = (id) => { setDeleteTargetId(id); setDeleteConfirmOpen(true); };
  const confirmDelete = async () => {
    if (deleteTargetId !== null) await handleDelete(deleteTargetId);
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleDelete = async (id) => {
    try {
      await deleteVehicle(id);
      showSnackbar("Vehicle deleted successfully", "success");
      await fetchTrucks();
    } catch (error) {
      showSnackbar("Failed to delete vehicle", "error");
      console.error(error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await fetchCustomers();
      const dict = response.data.reduce((acc, item) => { acc[item.id] = item.name; return acc; }, {});
      setCustomerOptions(dict);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  React.useEffect(() => { fetchTrucks(); loadCustomers(); }, []); // eslint-disable-line

  const fetchTrucks = async () => {
    try {
      const response = await fetchVehicles();
      setEmployees(response.data.map(v => ({ ...v, customer_name: haulierOptions[v.customer] || '' })));
    } catch (error) {
      console.error("Error fetching trucks:", error);
    }
  };

  // Re-map customer_name whenever haulierOptions loads
  React.useEffect(() => {
    setEmployees(prev => prev.map(v => ({ ...v, customer_name: haulierOptions[v.customer] || '' })));
  }, [haulierOptions]);

  React.useEffect(() => {
    if (location.state?.successMessage) {
      showSnackbar(location.state.successMessage, 'success');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = () => {
    const doc = new jsPDF('l', 'mm', 'a2');
    doc.setFontSize(18);
    doc.text('Vehicles Report', 14, 16);
    const tableData = employees.map(r => [
      r.imei_details?.imei, r.registration_number, r.model_name, r.model_year,
      r.chassis_number, r.engine_number, r.color, r.manufacturer, r.vehicle_type,
      r.remarks, haulierOptions[r.customer],
    ]);
    const cols = ['IMEI','Registration No.','Model Name','Model Year','Chassis No','Engine No','Color','Manufacturer','Vehicle Type','Remarks','Customer'];
    doc.autoTable({ head: [cols], body: tableData, startY: 30, styles: { fontSize: 9, cellPadding: 2 }, theme: 'grid' });
    doc.save('Vehicle Report.pdf');
  };


  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <DataTable
        title="Vehicle List"
        columns={COLUMNS}
        rows={employees}
        toolbarActions={
          <>
            <BulkUploadVehicles />
            <Button variant="contained" color="primary" onClick={() => navigate('/add-truck/new')}>Add Vehicle</Button>
            <Button variant="contained" color="primary" onClick={handleExport}>Export to PDF</Button>
          </>
        }
        renderActions={(employee) => (
          <>
            <IconButton color="secondary" size="small" onClick={() => navigate(`/add-truck/edit/${employee.id}`, { state: { record: employee } })} sx={{ p: '6px' }}>
              <EditIcon />
            </IconButton>
            <IconButton color="error" size="small" onClick={() => handleDeleteClick(employee.id)} sx={{ p: '6px', ml: '4px' }}>
              <DeleteIcon />
            </IconButton>
          </>
        )}
      />
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
